import React, { useEffect, useState, useCallback } from 'react';
import { User } from '../../types/auth';
import { usePredictionMarket, Market } from '../../hooks/usePredictionMarket';
import TxToast from '../common/TxToast';

interface TradePageProps {
  user: User | null;
}

export default function TradePage({ user }: TradePageProps) {
  const { markets, loadingMarkets, fetchMarkets, placeBet, getUserBet, tx, resetTx } =
    usePredictionMarket();

  const [search, setSearch] = useState('');
  const [selectedMarket, setSelectedMarket] = useState<Market | null>(null);
  const [prediction, setPrediction] = useState<boolean>(true);
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState('');
  // map: `${marketId}-yes` | `${marketId}-no` → ETH string
  const [userBets, setUserBets] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchMarkets();
  }, [fetchMarkets]);

  // Load user bets for all open markets when wallet/user changes
  const loadUserBets = useCallback(async () => {
    if (!user) return;
    const eth = (window as any).ethereum;
    if (!eth) return;
    try {
      const accounts: string[] = await eth.request({ method: 'eth_accounts' });
      if (!accounts.length) return;
      const addr = accounts[0];
      const results: Record<string, string> = {};
      for (const m of markets) {
        const [yes, no] = await Promise.all([
          getUserBet(m.id, addr, true),
          getUserBet(m.id, addr, false),
        ]);
        if (parseFloat(yes) > 0) results[`${m.id}-yes`] = yes;
        if (parseFloat(no) > 0) results[`${m.id}-no`] = no;
      }
      setUserBets(results);
    } catch (err: any) {
      console.debug('[TradePage] loadUserBets error:', err?.message);
    }
  }, [markets, getUserBet, user]);

  useEffect(() => {
    loadUserBets();
  }, [loadUserBets]);

  const filtered = markets.filter(m =>
    m.question.toLowerCase().includes(search.toLowerCase()),
  );

  function openTradeModal(market: Market) {
    setSelectedMarket(market);
    setPrediction(true);
    setAmount('');
    setAmountError('');
    resetTx();
  }

  function closeModal() {
    setSelectedMarket(null);
    resetTx();
  }

  async function handlePlaceBet(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedMarket) return;
    const val = parseFloat(amount);
    if (!amount || isNaN(val) || val <= 0) {
      setAmountError('Enter a valid amount in ETH (e.g. 0.01).');
      return;
    }
    setAmountError('');
    await placeBet(selectedMarket.id, prediction, amount);
    await loadUserBets();
  }

  function formatDeadline(ts: number) {
    return new Date(ts * 1000).toLocaleString();
  }

  function getOdds(yesPool: string, noPool: string) {
    const y = parseFloat(yesPool);
    const n = parseFloat(noPool);
    const total = y + n;
    if (total === 0) return { yes: 50, no: 50 };
    return {
      yes: Math.round((y / total) * 100),
      no: Math.round((n / total) * 100),
    };
  }

  function hasUserPosition(marketId: number) {
    return (
      parseFloat(userBets[`${marketId}-yes`] || '0') > 0 ||
      parseFloat(userBets[`${marketId}-no`] || '0') > 0
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h2 className="page-title">📊 Trade Markets</h2>
        <p className="page-subtitle">Browse open markets and place your predictions.</p>
      </div>

      {/* Search */}
      <div className="trade-search-row">
        <input
          className="form-input trade-search"
          type="text"
          placeholder="🔍 Search markets…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button className="btn-outline" onClick={fetchMarkets} disabled={loadingMarkets}>
          {loadingMarkets ? '⟳ Loading…' : '⟳ Refresh'}
        </button>
      </div>

      {/* Markets grid */}
      {loadingMarkets ? (
        <div className="loading-state">Loading markets from contract…</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          {markets.length === 0
            ? 'No markets found. Check your network connection and contract address.'
            : 'No markets match your search.'}
        </div>
      ) : (
        <div className="markets-grid">
          {filtered.map(m => {
            const odds = getOdds(m.yesPool, m.noPool);
            const isExpired = m.deadline > 0 && Date.now() / 1000 > m.deadline;
            const canTrade = !m.resolved && !isExpired && !!user;
            const hasPosition = hasUserPosition(m.id);
            return (
              <div
                key={m.id}
                className={`market-card${m.resolved ? ' market-card-resolved' : ''}`}
              >
                <div className="market-card-header">
                  <span className="market-id">#{m.id}</span>
                  {m.resolved ? (
                    <span className="badge badge-resolved">
                      Resolved: {m.outcome ? 'YES ✓' : 'NO ✗'}
                    </span>
                  ) : isExpired ? (
                    <span className="badge badge-expired">Expired</span>
                  ) : (
                    <span className="badge badge-open">Open</span>
                  )}
                </div>
                <p className="market-question">{m.question}</p>
                <div className="market-pools">
                  <span className="pool-yes">YES {odds.yes}%</span>
                  <div className="odds-bar">
                    <div
                      className="odds-fill-yes"
                      style={{ width: `${odds.yes}%` }}
                    />
                  </div>
                  <span className="pool-no">NO {odds.no}%</span>
                </div>
                <div className="market-meta">
                  <span>Pool: {(parseFloat(m.yesPool) + parseFloat(m.noPool)).toFixed(4)} ETH</span>
                  {m.deadline > 0 && (
                    <span>Expires: {formatDeadline(m.deadline)}</span>
                  )}
                </div>
                {hasPosition && (
                  <div className="user-position">
                    <span className="user-position-label">Your position:</span>
                    {parseFloat(userBets[`${m.id}-yes`] || '0') > 0 && (
                      <span className="position-yes">
                        YES {userBets[`${m.id}-yes`]} ETH
                      </span>
                    )}
                    {parseFloat(userBets[`${m.id}-no`] || '0') > 0 && (
                      <span className="position-no">
                        NO {userBets[`${m.id}-no`]} ETH
                      </span>
                    )}
                  </div>
                )}
                <button
                  className="btn-primary btn-full"
                  onClick={() => openTradeModal(m)}
                  disabled={!canTrade}
                  title={
                    !user
                      ? 'Sign in to trade'
                      : m.resolved
                      ? 'Market is resolved'
                      : isExpired
                      ? 'Market has expired'
                      : 'Place a prediction'
                  }
                >
                  {!user ? '🔒 Sign In to Trade' : m.resolved ? 'Resolved' : isExpired ? 'Expired' : '💰 Trade'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Trade Modal */}
      {selectedMarket && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Place Prediction</h3>
              <button className="modal-close" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>

            <p className="modal-market-question">{selectedMarket.question}</p>

            <TxToast tx={tx} onDismiss={resetTx} />

            <form onSubmit={handlePlaceBet} noValidate>
              {/* Yes / No toggle */}
              <div className="form-group">
                <label className="form-label">Your Prediction</label>
                <div className="prediction-toggle">
                  <button
                    type="button"
                    className={`toggle-btn toggle-yes${prediction ? ' active' : ''}`}
                    onClick={() => setPrediction(true)}
                  >
                    👍 YES
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn toggle-no${!prediction ? ' active' : ''}`}
                    onClick={() => setPrediction(false)}
                  >
                    👎 NO
                  </button>
                </div>
              </div>

              {/* Amount */}
              <div className="form-group">
                <label className="form-label" htmlFor="trade-amount">
                  Amount (ETH) <span className="required">*</span>
                </label>
                <input
                  id="trade-amount"
                  className={`form-input${amountError ? ' form-input-error' : ''}`}
                  type="number"
                  min="0.001"
                  step="0.001"
                  placeholder="e.g. 0.01"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                />
                {amountError && <p className="form-error">{amountError}</p>}
              </div>

              {/* Odds summary */}
              {(() => {
                const odds = getOdds(selectedMarket.yesPool, selectedMarket.noPool);
                return (
                  <div className="trade-odds-summary">
                    <span>Current odds — YES: {odds.yes}% / NO: {odds.no}%</span>
                    <span>
                      Total pool:{' '}
                      {(
                        parseFloat(selectedMarket.yesPool) +
                        parseFloat(selectedMarket.noPool)
                      ).toFixed(4)}{' '}
                      ETH
                    </span>
                  </div>
                );
              })()}

              <button
                type="submit"
                className="btn-primary btn-full"
                disabled={tx.status === 'pending'}
              >
                {tx.status === 'pending'
                  ? '⏳ Submitting…'
                  : `Place ${prediction ? 'YES' : 'NO'} Bet`}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
